import { useMutation, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useConflictHandler, isConflictHandledError } from '../context/ConflictHandlerContext';

/**
 * A wrapper around useMutation that automatically handles 409 conflicts.
 * 
 * When a 409 is received:
 * 1. The error is caught and not shown to the user
 * 2. User flow status is fetched from the server
 * 3. User is navigated to the correct page based on the flow status
 * 
 * Usage:
 * ```tsx
 * const { mutate, isPending } = useConflictSafeMutation({
 *   mutationFn: async (data) => {
 *     return await apiClient.api.someEndpoint(data);
 *   },
 *   onSuccess: () => {
 *     // Normal success handling
 *   },
 *   // onError will NOT be called for 409s - they're handled automatically
 * });
 * ```
 */
export function useConflictSafeMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { handleConflict } = useConflictHandler();

  const originalOnError = options.onError;

  return useMutation({
    ...options,
    onError: (error, variables, context) => {
      // Check if this is a 409 conflict (async handled internally)
      handleConflict(error).then((wasHandled) => {
        if (wasHandled) {
          // Don't call the original onError - conflict was handled silently
          console.log('[useConflictSafeMutation] 409 handled, skipping onError callback');
          return;
        }
        
        // For non-409 errors, call the original onError if provided
        if (originalOnError) {
          (originalOnError as any)(error, variables, context);
        }
      });
    },
  });
}

/**
 * Helper to wrap any async function with conflict handling.
 * Useful for one-off API calls outside of useMutation.
 * 
 * Usage:
 * ```tsx
 * const { withConflictHandler } = useConflictHandler();
 * 
 * const handleSubmit = async () => {
 *   try {
 *     await withConflictHandler(() => apiClient.api.someEndpoint());
 *   } catch (error) {
 *     if (!isConflictHandledError(error)) {
 *       // Handle non-409 errors
 *       showError(error);
 *     }
 *   }
 * };
 * ```
 */
export { isConflictHandledError };

